/**
 * PowerPoint (.pptx) → editable Free Style pages. Every slide becomes a `type: 'free'` page whose text boxes,
 * pictures, shapes, tables and simple charts are free elements at the same place (percent of a 16:9 stage;
 * 4:3 decks are letterboxed). Text keeps its words, size, colour, weight, italics, font (when it is one of our
 * fonts), alignment and bullets; placeholders inherit position and text style from the layout and master, as
 * PowerPoint does. Master and layout artwork (logos, bands) is drawn under the slide's own shapes.
 *
 * Pictures come back as `pptx-media:<zip path>` URLs plus the files to upload; the caller swaps in the real URLs.
 */
import JSZip from 'jszip';
import { runsToMarkup, type Run } from '@/cowork/omniad/components/slides/richTextModel';
import { FONT_CHOICES, newId } from '@/cowork/omniad/components/slides/elements';

type Slide = Record<string, any>;
export interface PptxResult {
  title: string;
  slides: Slide[];
  /** Zip path → picture file to upload (only pictures a page uses). */
  media: Map<string, File>;
  /** What could not be brought over (e.g. "2 EMF pictures"). */
  skipped: string[];
}
export const PPTX_MEDIA = 'pptx-media:';

// ─── XML helpers ────────────────────────────────────────────────────────────

const parseXml = (s: string) => new DOMParser().parseFromString(s, 'application/xml');
const kids = (el: Element | null | undefined, name?: string): Element[] => (el ? Array.from(el.children).filter((c) => !name || c.localName === name) : []);
const kid = (el: Element | null | undefined, name: string): Element | null => kids(el, name)[0] || null;
const at = (el: Element | null | undefined, ...names: string[]): Element | null => names.reduce<Element | null>((e, n) => kid(e, n), el ?? null);
const all = (el: Element | Document | null | undefined, name: string): Element[] => (el ? Array.from(el.getElementsByTagNameNS('*', name)) : []);
const num = (v: string | null | undefined, d = 0) => { const n = Number(v); return v != null && v !== '' && Number.isFinite(n) ? n : d; };
const attrNS = (el: Element | null, local: string) => (el ? el.getAttribute(`r:${local}`) ?? Array.from(el.attributes).find((a) => a.localName === local)?.value ?? null : null);

function resolvePath(fromPart: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1);
  const parts = fromPart.split('/'); parts.pop();
  for (const seg of target.split('/')) { if (seg === '..') parts.pop(); else if (seg !== '.') parts.push(seg); }
  return parts.join('/');
}

// ─── Colours ────────────────────────────────────────────────────────────────

const hex2 = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b); const min = Math.min(r, g, b); const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min; const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}
function hslToHex(h: number, s: number, l: number): string {
  if (s === 0) { const v = l * 255; return `#${hex2(v)}${hex2(v)}${hex2(v)}`.toUpperCase(); }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s; const p = 2 * l - q;
  const f = (t: number) => { if (t < 0) t += 1; if (t > 1) t -= 1; return t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p; };
  return `#${hex2(f(h + 1 / 3) * 255)}${hex2(f(h) * 255)}${hex2(f(h - 1 / 3) * 255)}`.toUpperCase();
}
/** Apply lumMod / lumOff / tint / shade (the "lighter 40%" family) to a hex colour. */
function modify(hex: string, clr: Element): string {
  let r = parseInt(hex.slice(1, 3), 16); let g = parseInt(hex.slice(3, 5), 16); let b = parseInt(hex.slice(5, 7), 16);
  for (const m of kids(clr)) {
    const v = num(m.getAttribute('val'), 100000) / 100000;
    if (m.localName === 'tint') { r += (255 - r) * (1 - v); g += (255 - g) * (1 - v); b += (255 - b) * (1 - v); }
    if (m.localName === 'shade') { r *= v; g *= v; b *= v; }
  }
  const mods = kids(clr).filter((m) => m.localName === 'lumMod' || m.localName === 'lumOff');
  if (!mods.length) return `#${hex2(r)}${hex2(g)}${hex2(b)}`.toUpperCase();
  const [h, s, l0] = rgbToHsl(r, g, b); let l = l0;
  for (const m of mods) { const v = num(m.getAttribute('val')) / 100000; if (m.localName === 'lumMod') l *= v; else l += v; }
  return hslToHex(h, s, Math.max(0, Math.min(1, l)));
}

interface Scheme { colors: Record<string, string>; major: string; minor: string }

function readScheme(themeDoc: Document | null, clrMap: Element | null): Scheme {
  const colors: Record<string, string> = {};
  const cs = all(themeDoc, 'clrScheme')[0];
  for (const c of kids(cs)) {
    const v = kid(c, 'srgbClr')?.getAttribute('val') || kid(c, 'sysClr')?.getAttribute('lastClr');
    if (v) colors[c.localName] = `#${v.toUpperCase()}`;
  }
  const map: Record<string, string> = { bg1: 'lt1', tx1: 'dk1', bg2: 'lt2', tx2: 'dk2' };
  if (clrMap) for (const k of Object.keys(map)) { const v = clrMap.getAttribute(k); if (v) map[k] = v; }
  for (const [alias, real] of Object.entries(map)) if (colors[real]) colors[alias] = colors[real];
  const font = (which: string) => all(themeDoc, which)[0] ? kid(all(themeDoc, which)[0], 'latin')?.getAttribute('typeface') || '' : '';
  return { colors, major: font('majorFont'), minor: font('minorFont') };
}

/** The colour of an element that holds one colour child (srgbClr, schemeClr, sysClr, prstClr); null when none. */
function colorIn(el: Element | null, scheme: Scheme): string | null {
  if (!el) return null;
  for (const c of kids(el)) {
    let base: string | null = null;
    if (c.localName === 'srgbClr') base = `#${(c.getAttribute('val') || '000000').toUpperCase()}`;
    else if (c.localName === 'schemeClr') base = scheme.colors[c.getAttribute('val') || ''] || null;
    else if (c.localName === 'sysClr') base = `#${(c.getAttribute('lastClr') || '000000').toUpperCase()}`;
    else if (c.localName === 'prstClr') base = ({ black: '#000000', white: '#FFFFFF', red: '#FF0000', blue: '#0000FF', green: '#008000', yellow: '#FFFF00', gray: '#808080' } as Record<string, string>)[c.getAttribute('val') || ''] || null;
    if (base && /^#[0-9A-F]{6}$/.test(base)) {
      const alpha = kid(c, 'alpha'); if (alpha && num(alpha.getAttribute('val'), 100000) < 20000) return null; // nearly transparent
      return modify(base, c);
    }
  }
  return null;
}
/** A fill (spPr / bgPr / tcPr): solid colour, first gradient stop, or a picture. */
function fillOf(el: Element | null, scheme: Scheme): { color?: string; blip?: string; none?: boolean } | null {
  if (!el) return null;
  if (kid(el, 'noFill')) return { none: true };
  const solid = kid(el, 'solidFill'); if (solid) { const c = colorIn(solid, scheme); return c ? { color: c } : { none: true }; }
  const grad = kid(el, 'gradFill'); if (grad) { const gs = all(grad, 'gs')[0]; const c = colorIn(gs, scheme); return c ? { color: c } : null; }
  const blip = at(el, 'blipFill', 'blip'); if (blip) return { blip: attrNS(blip, 'embed') || undefined };
  return null;
}

// ─── Parts ──────────────────────────────────────────────────────────────────

interface Part { path: string; doc: Document; rels: Map<string, { target: string; type: string }> }

async function loadPart(zip: JSZip, path: string, cache: Map<string, Part | null>): Promise<Part | null> {
  if (cache.has(path)) return cache.get(path)!;
  const f = zip.file(path);
  if (!f) { cache.set(path, null); return null; }
  const doc = parseXml(await f.async('string'));
  const rels = new Map<string, { target: string; type: string }>();
  const relPath = path.replace(/([^/]+)$/, '_rels/$1.rels');
  const rf = zip.file(relPath);
  if (rf) for (const r of all(parseXml(await rf.async('string')), 'Relationship')) {
    const mode = r.getAttribute('TargetMode');
    rels.set(r.getAttribute('Id') || '', { target: mode === 'External' ? '' : resolvePath(path, r.getAttribute('Target') || ''), type: (r.getAttribute('Type') || '').split('/').pop() || '' });
  }
  const part = { path, doc, rels };
  cache.set(path, part);
  return part;
}
const relOfType = (p: Part | null, type: string) => (p ? [...p.rels.values()].find((r) => r.type === type)?.target || null : null);

// ─── Placeholders ───────────────────────────────────────────────────────────

const phOf = (shape: Element) => all(at(shape, 'nvSpPr') || at(shape, 'nvPicPr') || at(shape, 'nvGraphicFramePr'), 'ph')[0] || null;
const phKind = (t: string | null) => (t === 'ctrTitle' || t === 'title' ? 'title' : t === 'dt' || t === 'ftr' || t === 'sldNum' ? t : 'body');
/** The layout / master shape a placeholder inherits from (by idx, then by type). */
function findPh(tree: Document | null, ph: Element): Element | null {
  if (!tree) return null;
  const type = ph.getAttribute('type') || 'body'; const idx = ph.getAttribute('idx');
  const shapes = all(tree, 'sp');
  const withPh = shapes.map((s) => ({ s, p: phOf(s) })).filter((x) => x.p);
  if (idx != null) { const m = withPh.find((x) => x.p!.getAttribute('idx') === idx); if (m) return m.s; }
  const m = withPh.find((x) => (x.p!.getAttribute('type') || 'body') === type) || withPh.find((x) => phKind(x.p!.getAttribute('type')) === phKind(type));
  return m?.s || null;
}

// ─── Geometry ───────────────────────────────────────────────────────────────

interface Box { x: number; y: number; w: number; h: number; rot: number }
type Transform = (b: Box) => Box;
const identity: Transform = (b) => b;
function xfrmBox(xfrm: Element | null): Box | null {
  const off = kid(xfrm, 'off'); const ext = kid(xfrm, 'ext');
  if (!off || !ext) return null;
  return { x: num(off.getAttribute('x')), y: num(off.getAttribute('y')), w: num(ext.getAttribute('cx')), h: num(ext.getAttribute('cy')), rot: num(xfrm!.getAttribute('rot')) / 60000 };
}
/** Group transform: child coordinates (chOff/chExt) → the group's frame. */
function groupTransform(grpSpPr: Element | null, outer: Transform): Transform {
  const x = at(grpSpPr, 'xfrm'); const b = xfrmBox(x);
  const chOff = kid(x, 'chOff'); const chExt = kid(x, 'chExt');
  if (!b || !chOff || !chExt) return outer;
  const cx = num(chOff.getAttribute('x')); const cy = num(chOff.getAttribute('y'));
  const sx = num(chExt.getAttribute('cx'), 1) ? b.w / num(chExt.getAttribute('cx'), 1) : 1;
  const sy = num(chExt.getAttribute('cy'), 1) ? b.h / num(chExt.getAttribute('cy'), 1) : 1;
  return (c) => outer({ x: b.x + (c.x - cx) * sx, y: b.y + (c.y - cy) * sy, w: c.w * sx, h: c.h * sy, rot: c.rot + b.rot });
}

// ─── The deck ───────────────────────────────────────────────────────────────

const FONT_BY_NAME = new Map(FONT_CHOICES.map((f) => [f.family.toLowerCase(), f.family]));
const IMAGE_TYPES: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp' };

export async function parsePptx(data: ArrayBuffer | Uint8Array | Blob, fileName = 'Imported deck'): Promise<PptxResult> {
  const zip = await JSZip.loadAsync(data);
  const cache = new Map<string, Part | null>();
  const pres = await loadPart(zip, 'ppt/presentation.xml', cache);
  if (!pres) throw new Error('This is not a PowerPoint file (ppt/presentation.xml is missing).');
  const sz = all(pres.doc, 'sldSz')[0];
  const W = num(sz?.getAttribute('cx'), 12192000); const H = num(sz?.getAttribute('cy'), 6858000);
  // Fit the slide into the 16:9 stage (4:3 decks are pillarboxed).
  const aspect = W / H; const stage = 16 / 9;
  const fx = aspect <= stage ? aspect / stage : 1; const fy = aspect <= stage ? 1 : stage / aspect;
  const ox = ((1 - fx) / 2) * 100; const oy = ((1 - fy) / 2) * 100;
  const pct = (b: Box) => ({ x: ox + (b.x / W) * 100 * fx, y: oy + (b.y / H) * 100 * fy, w: (b.w / W) * 100 * fx, h: (b.h / H) * 100 * fy });
  const ptToCqw = (pt: number) => (pt / (W / 12700)) * 100 * fx;
  const r2 = (n: number) => Math.round(n * 100) / 100;

  const media = new Map<string, File>(); const skipped = new Map<string, number>();
  const skip = (what: string) => skipped.set(what, (skipped.get(what) || 0) + 1);
  const mediaRef = (part: Part, rId: string | null | undefined): string | null => {
    const target = rId ? part.rels.get(rId)?.target : null;
    if (!target) return null;
    const ext = (target.split('.').pop() || '').toLowerCase();
    const type = IMAGE_TYPES[ext];
    if (!type) { skip(`${ext.toUpperCase()} pictures`); return null; }
    if (!media.has(target)) media.set(target, new File([], `pptx-${media.size + 1}.${ext}`, { type })); // content filled below
    return `${PPTX_MEDIA}${target}`;
  };

  const slideIds = all(pres.doc, 'sldId');
  const slides: Slide[] = [];
  for (const sid of slideIds) {
    const target = pres.rels.get(attrNS(sid, 'id') || '')?.target;
    const slide = target ? await loadPart(zip, target, cache) : null;
    if (!slide) continue;
    if (slide.doc.documentElement.getAttribute('show') === '0') continue; // hidden slide
    const layout = await loadPart(zip, relOfType(slide, 'slideLayout') || '', cache);
    const master = await loadPart(zip, relOfType(layout, 'slideMaster') || '', cache);
    const themeDoc = (await loadPart(zip, relOfType(master, 'theme') || '', cache))?.doc || null;
    const scheme = readScheme(themeDoc, all(master?.doc, 'clrMap')[0] || null);
    const override = all(slide.doc, 'clrMapOvr')[0] ? all(all(slide.doc, 'clrMapOvr')[0], 'overrideClrMapping')[0] : null;
    const sch = override ? readScheme(themeDoc, override) : scheme;
    const txStyles = all(master?.doc, 'txStyles')[0] || null;

    const elements: any[] = [];
    let z = 1;
    const push = (e: any) => { elements.push({ id: newId(), z: z++, ...e }); };

    // ── Text ──
    /** Paragraph-level style chain for a shape: its own lstStyle, the inherited placeholders' lstStyles, then the master text style. */
    const styleChain = (shape: Element, inherited: Element[], kind: string, lvl: number): Element[] => {
      const lv = `lvl${lvl + 1}pPr`;
      const out: Element[] = [];
      for (const s of [shape, ...inherited]) { const p = at(s, 'txBody', 'lstStyle', lv); if (p) out.push(p); }
      const ms = kind === 'title' ? kid(txStyles, 'titleStyle') : kind === 'body' ? kid(txStyles, 'bodyStyle') : kid(txStyles, 'otherStyle');
      const mp = kid(ms, lv); if (mp) out.push(mp);
      return out;
    };
    const firstAttr = (els: Array<Element | null>, name: string) => { for (const e of els) { const v = e?.getAttribute(name); if (v != null && v !== '') return v; } return null; };
    const firstKid = (els: Array<Element | null>, name: string) => { for (const e of els) { const k = kid(e, name); if (k) return k; } return null; };
    const typeface = (rprs: Array<Element | null>) => {
      let tf = at(rprs.find((r) => kid(r, 'latin')) || null, 'latin')?.getAttribute('typeface') || '';
      if (tf === '+mj-lt') tf = sch.major; else if (tf === '+mn-lt') tf = sch.minor;
      return tf;
    };

    const addText = (shape: Element, frame: { x: number; y: number; w: number; h: number }, rot: number, inherited: Element[], kind: string) => {
      const body = kid(shape, 'txBody'); if (!body) return;
      const paras = kids(body, 'p');
      type R = Run & { pt: number; color: string | null; font: string };
      const runs: R[] = []; let align: string | null = null; let lineHeight: number | null = null; let allCaps = true; let chars = 0;
      paras.forEach((p, pi) => {
        const pPr = kid(p, 'pPr'); const lvl = num(pPr?.getAttribute('lvl'));
        const chain = styleChain(shape, inherited, kind, lvl);
        const pChain = [pPr, ...chain];
        if (pi > 0) runs.push({ text: '\n', pt: 0, color: null, font: '' });
        if (align == null) align = firstAttr(pChain, 'algn');
        if (lineHeight == null) { const sp = firstKid(pChain, 'lnSpc'); const pctV = at(sp, 'spcPct')?.getAttribute('val'); if (pctV) lineHeight = num(pctV) / 100000; }
        // The nearest level that says anything about bullets decides.
        let bu: Element | null = null;
        for (const e of pChain) { if (kid(e, 'buNone')) break; const k = kid(e, 'buChar') || kid(e, 'buAutoNum'); if (k) { bu = k; break; } }
        const pieces = kids(p).filter((c) => c.localName === 'r' || c.localName === 'br' || c.localName === 'fld');
        const hasText = pieces.some((c) => (kid(c, 't')?.textContent || '').trim());
        if (bu && hasText) runs.push({ text: bu.localName === 'buAutoNum' ? `${pi + 1}. ` : '• ', pt: 0, color: null, font: '' });
        for (const c of pieces) {
          if (c.localName === 'br') { runs.push({ text: '\n', pt: 0, color: null, font: '' }); continue; }
          const t = kid(c, 't')?.textContent || ''; if (!t) continue;
          const rPr = kid(c, 'rPr');
          const rChain = [rPr, kid(pPr, 'defRPr'), ...chain.map((e) => kid(e, 'defRPr'))];
          const pt = num(firstAttr(rChain, 'sz'), kind === 'title' ? 4000 : 1800) / 100;
          const b = (firstAttr(rChain, 'b') ?? '0') === '1' || firstAttr(rChain, 'b') === 'true';
          const it = (firstAttr(rChain, 'i') ?? '0') === '1' || firstAttr(rChain, 'i') === 'true';
          const color = colorIn(firstKid(rChain, 'solidFill'), sch);
          const tf = typeface(rChain);
          if (firstAttr(rChain, 'cap') !== 'all') allCaps = false;
          runs.push({ text: t, b, it, pt, color, font: FONT_BY_NAME.get(tf.toLowerCase()) || '' });
          chars += t.length;
        }
      });
      if (!chars) return;
      // The dominant size / colour / font become the element's; runs that differ keep their own.
      const tally = <K>(get: (r: R) => K) => { const m = new Map<K, number>(); for (const r of runs) if (r.text !== '\n' && r.pt) m.set(get(r), (m.get(get(r)) || 0) + r.text.length); return [...m.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]; };
      const basePt = tally((r) => r.pt) || 18; const baseColor = tally((r) => r.color) ?? null; const baseFont = tally((r) => r.font) || '';
      const textRuns = runs.filter((r) => r.text !== '\n' && r.pt);
      const allBold = textRuns.every((r) => r.b);
      const markup = runsToMarkup(runs.map((r) => {
        if (r.text === '\n' || !r.pt) return { text: r.text };
        const st: Run['st'] = {};
        if (r.color && r.color !== baseColor) st.c = r.color;
        if (r.font && r.font !== baseFont) st.f = r.font;
        return { text: r.text, b: allBold ? false : r.b, it: r.it, sz: r.pt >= basePt * 1.15 ? 'up' : r.pt <= basePt * 0.87 ? 'down' : undefined, st };
      }));
      const bodyPr = [kid(body, 'bodyPr'), ...inherited.map((s) => at(s, 'txBody', 'bodyPr'))];
      const anchor = firstAttr(bodyPr, 'anchor') || (kind === 'title' ? 'ctr' : 't');
      const ins = num(firstAttr(bodyPr, 'lIns'), 91440);
      push({
        // A little headroom: our fonts rarely match PowerPoint's metrics exactly, and a box clips what overflows.
        kind: 'text', ...frame, h: r2(Math.min(frame.h * 1.12 + 0.6, 100 - frame.y)), rotate: rot ? r2(rot) : undefined, text: markup,
        size: r2(ptToCqw(basePt)), color: baseColor || undefined, font: baseFont || undefined, bold: allBold || undefined,
        align: align === 'ctr' ? 'center' : align === 'r' ? 'right' : 'left', valign: anchor === 'ctr' ? 'middle' : anchor === 'b' ? 'bottom' : 'top',
        lineHeight: lineHeight ? r2(Math.max(0.9, Math.min(2, lineHeight * 1.2))) : undefined, uppercase: allCaps || undefined,
        pad: r2(ptToCqw(ins / 12700)),
      });
    };

    // ── Shapes ──
    const drawTree = async (part: Part, tree: Element | null, tf: Transform, opts: { placeholders: boolean; inheritFrom: Array<Document | null> }) => {
      for (const node of kids(tree)) {
        const name = node.localName;
        if (at(node, 'nvSpPr', 'cNvPr')?.getAttribute('hidden') === '1' || at(node, 'nvPicPr', 'cNvPr')?.getAttribute('hidden') === '1') continue;
        if (name === 'grpSp') { await drawTree(part, node, groupTransform(kid(node, 'grpSpPr'), tf), opts); continue; }
        const ph = phOf(node);
        if (ph && !opts.placeholders) continue; // layout / master prompts are not drawn
        const inherited = ph ? opts.inheritFrom.map((d) => findPh(d, ph)).filter((x): x is Element => !!x) : [];
        const spPr = kid(node, 'spPr') || kid(node, 'grpSpPr');
        let box = xfrmBox(at(spPr, 'xfrm')) || xfrmBox(at(node, 'xfrm'));
        for (const s of inherited) { if (box) break; box = xfrmBox(at(s, 'spPr', 'xfrm')); }
        if (!box) continue;
        const b = tf(box); const frame = pct(b);
        frame.x = r2(frame.x); frame.y = r2(frame.y); frame.w = r2(Math.max(frame.w, 0.3)); frame.h = r2(Math.max(frame.h, 0.3));
        const rot = b.rot;

        if (name === 'pic') {
          const url = mediaRef(part, attrNS(at(node, 'blipFill', 'blip'), 'embed'));
          if (url) push({ kind: 'image', ...frame, rotate: rot ? r2(rot) : undefined, media: { url, kind: 'image', fit: 'cover' } });
          continue;
        }
        if (name === 'graphicFrame') {
          const tbl = all(node, 'tbl')[0];
          if (tbl) {
            const rows = all(tbl, 'tr').map((tr) => all(tr, 'tc').map((tc) => all(tc, 't').map((t) => t.textContent || '').join('').trim()));
            const text = rows.map((cells, i) => (i === 0 ? `**${cells.join('  ·  ')}**` : cells.join('  ·  '))).join('\n');
            if (text.trim()) push({ kind: 'text', ...frame, text, size: r2(ptToCqw(12)), background: '#F9FAFB', pad: 0.8, lineHeight: 1.5 });
            continue;
          }
          const chartRel = attrNS(all(node, 'chart')[0] || null, 'id');
          const chartPath = chartRel ? part.rels.get(chartRel)?.target : null;
          const chart = chartPath ? await loadPart(zip, chartPath, cache) : null;
          const plot = all(chart?.doc, 'plotArea')[0];
          const kindEl = kids(plot).find((k) => /Chart$/.test(k.localName));
          if (kindEl) {
            const type = kindEl.localName.startsWith('pie') || kindEl.localName.startsWith('doughnut') ? (kindEl.localName.startsWith('doughnut') ? 'donut' : 'pie') : kindEl.localName.startsWith('line') || kindEl.localName.startsWith('area') ? 'line' : kid(kindEl, 'barDir')?.getAttribute('val') === 'bar' ? 'hbar' : 'bar';
            const sers = kids(kindEl, 'ser');
            const labels = all(kid(sers[0], 'cat'), 'pt').map((p) => kid(p, 'v')?.textContent || '');
            const datasets = sers.map((s) => ({ label: all(kid(s, 'tx'), 'v')[0]?.textContent || undefined, values: all(kid(s, 'val'), 'pt').map((p) => num(kid(p, 'v')?.textContent)) }));
            const title = all(all(chart?.doc, 'title')[0], 't').map((t) => t.textContent).join('') || undefined;
            if (datasets.some((d) => d.values.length)) push({ kind: 'chart', ...frame, title, chart: { type, labels, datasets } });
          } else skip('SmartArt / diagrams');
          continue;
        }
        if (name !== 'sp' && name !== 'cxnSp') continue;

        const geom = at(spPr, 'prstGeom')?.getAttribute('prst') || (name === 'cxnSp' ? 'line' : 'rect');
        const styleEl = kid(node, 'style');
        const fill = fillOf(spPr, sch) ?? (styleEl && at(styleEl, 'fillRef') && num(at(styleEl, 'fillRef')!.getAttribute('idx')) > 0 ? { color: colorIn(at(styleEl, 'fillRef'), sch) || undefined } : null);
        const ln = kid(spPr, 'ln');
        const lineColor = ln && !kid(ln, 'noFill') ? colorIn(kid(ln, 'solidFill'), sch) : (styleEl && !ln ? colorIn(at(styleEl, 'lnRef'), sch) : null);
        const lineW = ln ? num(ln.getAttribute('w'), 12700) : 12700;
        if (geom === 'line' || geom.startsWith('straightConnector') || name === 'cxnSp') {
          if (lineColor) push({ kind: 'shape', shape: 'line', ...frame, h: Math.max(frame.h, 0.6), rotate: rot ? r2(rot) : undefined, fill: lineColor, strokeWidth: r2(Math.max(0.05, ptToCqw(lineW / 12700) / 2)) });
          continue;
        }
        if (fill?.blip) {
          const url = mediaRef(part, fill.blip);
          if (url) push({ kind: 'image', ...frame, rotate: rot ? r2(rot) : undefined, media: { url, kind: 'image', fit: 'cover' } });
        } else if (fill?.color || lineColor) {
          const shape = geom === 'ellipse' ? 'circle' : 'rect';
          push({ kind: 'shape', shape, ...frame, rotate: rot ? r2(rot) : undefined, fill: fill?.color || 'transparent', stroke: lineColor || undefined, strokeWidth: lineColor ? r2(Math.max(0.05, ptToCqw(lineW / 12700))) : undefined, radius: geom === 'roundRect' ? 1.2 : 0 });
        }
        addText(node, frame, rot, inherited, ph ? phKind(ph.getAttribute('type')) : 'other');
      }
    };

    // Background: the slide's, else the layout's, else the master's.
    let bgColor: string | undefined; let bgImage: string | null = null;
    for (const p of [slide, layout, master]) {
      const bg = p ? all(p.doc, 'bg')[0] : null; if (!bg) continue;
      const bgPr = kid(bg, 'bgPr'); const bgRef = kid(bg, 'bgRef');
      const f = bgPr ? fillOf(bgPr, sch) : bgRef ? { color: colorIn(bgRef, sch) || undefined } : null;
      if (f?.blip) { bgImage = mediaRef(p!, f.blip); break; }
      if (f?.color) { bgColor = f.color; break; }
    }
    if (bgImage) push({ kind: 'image', x: ox, y: oy, w: 100 * fx, h: 100 * fy, media: { url: bgImage, kind: 'image', fit: 'cover' } });
    const showMaster = (d: Document | null | undefined) => d?.documentElement.getAttribute('showMasterSp') !== '0';
    if (master && layout && showMaster(slide.doc) && showMaster(layout.doc)) await drawTree(master, all(master.doc, 'spTree')[0], identity, { placeholders: false, inheritFrom: [] });
    if (layout && showMaster(slide.doc)) await drawTree(layout, all(layout.doc, 'spTree')[0], identity, { placeholders: false, inheritFrom: [] });
    await drawTree(slide, all(slide.doc, 'spTree')[0], identity, { placeholders: true, inheritFrom: [layout?.doc || null, master?.doc || null] });

    // Speaker notes.
    const notesPart = await loadPart(zip, relOfType(slide, 'notesSlide') || '', cache);
    const notesBody = notesPart ? all(notesPart.doc, 'sp').find((s) => phOf(s)?.getAttribute('type') === 'body') : null;
    const notes = notesBody ? all(notesBody, 'p').map((p) => all(p, 't').map((t) => t.textContent || '').join('')).join('\n').trim() : '';

    slides.push({ type: 'free', bgColor: bgColor || '#FFFFFF', beatlyMark: false, _from: { import: 'pptx' }, elements: elements.map((e) => Object.fromEntries(Object.entries(e).filter(([, v]) => v !== undefined))), ...(notes ? { notes } : {}) });
  }
  if (!slides.length) throw new Error('No slides found in this PowerPoint file.');

  // Fill the picture files.
  for (const [path, stub] of [...media.entries()]) {
    const f = zip.file(path);
    if (!f) { media.delete(path); continue; }
    media.set(path, new File([await f.async('blob')], stub.name, { type: stub.type }));
  }
  const core = zip.file('docProps/core.xml');
  const docTitle = core ? all(parseXml(await core.async('string')), 'title')[0]?.textContent?.trim() : '';
  return {
    title: docTitle || fileName.replace(/\.[^.]+$/, ''),
    slides,
    media,
    skipped: [...skipped.entries()].map(([what, n]) => `${n} ${what}`),
  };
}
